import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Link as RouterLink } from "react-router-dom";

import {
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { apiOutletClient, formatDate, formatNumber, getErrorMessage } from "@/features/shared";
import { CustomDialogTitle } from "@/features/ui/custom-dialog-title";

const JournalGroup = ({ journalVoucher }) => {
  const { hasPermission } = useAuth();
  const canViewJournalVoucher = hasPermission(
    PermissionEnum.READ_JOURNAL_VOUCHERS
  );

  const transactions = useMemo(
    () => journalVoucher.outletChartOfAccountTransactions || [],
    [journalVoucher.outletChartOfAccountTransactions]
  );

  const totalDebit = useMemo(
    () =>
      transactions.reduce(
        (sum, t) => (t.amount >= 0 ? sum + t.amount : sum),
        0
      ),
    [transactions]
  );

  const totalCredit = useMemo(
    () =>
      transactions.reduce(
        (sum, t) => (t.amount < 0 ? sum + Math.abs(t.amount) : sum),
        0
      ),
    [transactions]
  );

  return (
    <Stack spacing={2} sx={{ minHeight: 0, flex: 1 }}>
      <Grid container spacing={1}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Stack direction="row" spacing={1}>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                minWidth: 90
              }}>
              Tanggal
            </Typography>
            <Typography variant="body2">
              : {formatDate(journalVoucher.transactionDate)}
            </Typography>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ justifyContent: { sm: "flex-end" } }}
          >
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Nomor #
            </Typography>
            <Typography variant="body2" sx={{
              fontWeight: "medium"
            }}>
              :{" "}
              {canViewJournalVoucher ? (
                <Link
                  component={RouterLink}
                  to={`/general-ledger/journal-vouchers/${journalVoucher.referenceNumber}`}
                  underline="hover"
                >
                  {journalVoucher.referenceNumber}
                </Link>
              ) : (
                journalVoucher.referenceNumber
              )}
            </Typography>
          </Stack>
        </Grid>
        {journalVoucher.note && (
          <Grid size={{ xs: 12 }}>
            <Stack direction="row" spacing={1}>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  minWidth: 90
                }}>
                Keterangan
              </Typography>
              <Typography variant="body2">: {journalVoucher.note}</Typography>
            </Stack>
          </Grid>
        )}
      </Grid>
      <TableContainer sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <Table size="small" stickyHeader>
          <colgroup>
            <col />
            <col style={{ width: 150 }} />
            <col style={{ width: 150 }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell>Akun Perkiraan</TableCell>
              <TableCell align="right">Debit</TableCell>
              <TableCell align="right">Kredit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction) => {
              const isDebit = transaction.amount >= 0;
              return (
                <TableRow key={transaction.id}>
                  <TableCell sx={{ pl: isDebit ? 2 : 6 }}>
                    <Typography variant="body2">
                      {transaction.outletChartOfAccount.code}{" "}
                      {transaction.outletChartOfAccount.name}
                    </Typography>
                    {transaction.note && (
                      <Typography variant="caption" sx={{
                        color: "text.secondary"
                      }}>
                        {transaction.note}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {isDebit ? formatNumber(transaction.amount) : ""}
                  </TableCell>
                  <TableCell align="right">
                    {!isDebit ? formatNumber(Math.abs(transaction.amount)) : ""}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider />
      <Table size="small">
        <colgroup>
          <col />
          <col style={{ width: 150 }} />
          <col style={{ width: 150 }} />
        </colgroup>
        <TableBody>
          <TableRow sx={{ "& td": { borderBottom: "none" } }}>
            <TableCell>
              <Typography variant="caption" sx={{
                color: "text.secondary"
              }}>
                ( Jumlah ditampilkan dalam IDR )
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2" sx={{
                fontWeight: "bold"
              }}>
                {formatNumber(totalDebit)}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2" sx={{
                fontWeight: "bold"
              }}>
                {formatNumber(totalCredit)}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Stack>
  );
};

JournalGroup.propTypes = {
  journalVoucher: PropTypes.object.isRequired,
};

export const JournalDetailsDialog = ({
  open,
  onClose,
  transactionableType,
  transactionableId,
}) => {
  const [loading, setLoading] = useState(false);
  const [journalVouchers, setJournalVouchers] = useState([]);
  const [error, setError] = useState(null);

  const loadJournalVouchers = useCallback(async () => {
    if (!open || !transactionableType || !transactionableId) return;

    setLoading(true);
    setJournalVouchers([]);
    setError(null);
    try {
      const {
        data: { data },
      } = await apiOutletClient.get("journal-vouchers/by-transactionable", {
        params: {
          transactionableType,
          transactionableId,
        },
      });
      setJournalVouchers(Array.isArray(data) ? data : [data]);
    } catch (error) {
      setError(
        getErrorMessage(error)
      );
    } finally {
      setLoading(false);
    }
  }, [open, transactionableType, transactionableId]);

  useEffect(() => {
    loadJournalVouchers();
  }, [loadJournalVouchers]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: "80vh", display: "flex", flexDirection: "column" },
      }}
    >
      <CustomDialogTitle title="Rincian Jurnal" onClose={onClose} />
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        {loading && (
          <Stack
            sx={{
              alignItems: "center",
              py: 4
            }}>
            <CircularProgress />
          </Stack>
        )}
        {!loading && error && (
          <Typography
            color="error"
            sx={{
              textAlign: "center",
              py: 4
            }}>
            {error}
          </Typography>
        )}
        {!loading && !error && journalVouchers.length === 0 && (
          <Typography
            sx={{
              color: "text.secondary",
              textAlign: "center",
              py: 4
            }}>
            Tidak ada data jurnal.
          </Typography>
        )}
        {!loading && !error && journalVouchers.length > 0 && (
          <Stack
            spacing={4}
            sx={{
              pb: 2,
              minHeight: 0,
              flex: 1
            }}>
            {journalVouchers.map((journalVoucher, index) => (
              <Stack
                key={journalVoucher.id}
                spacing={2}
                sx={{ minHeight: 0, flex: 1 }}
              >
                {index > 0 && <Divider />}
                <JournalGroup journalVoucher={journalVoucher} />
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

JournalDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  transactionableType: PropTypes.string.isRequired,
  transactionableId: PropTypes.string,
};
